import { or, readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { computed } from '@ember/object';

import SessionService from 'cio/services/session';
import UserModel from 'cio/models/user';
import {
  USER_ROLE_ADMIN,
  USER_ROLE_MANAGER,
  USER_ROLE_AUTHOR,
  UserRoleType,
  USER_ROLE_VIEWER,
  USER_ROLE_AUTHOR_REDACT,
  USER_ROLE_VIEWER_REDACT,
} from 'cio/utils/user-roles';
import ApiService from 'cio/services/api';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import Ember from 'ember';
import { ONE_HOUR_IN_SECONDS, ONE_MINUTE_IN_SECONDS } from 'cio/utils/time-constants';
import { throttle } from '@ember/runloop';
import MasqService from 'cio/services/masq';
import {
  getAccessExpirationCookie,
  refreshAuthCookies,
  removeAccessCookie,
  removeAccessExpirationCookie,
  setAccessCookie,
  setAccessExpirationCookie,
} from 'cio/utils/auth-cookies';
import { unixNowSeconds } from 'cio/utils/time';
import { tracked } from '@glimmer/tracking';
import { isValidPermission, Permission, Relation } from '@customerio/hydra-permissions';

const REAUTHENTICATION_REQUEST_TIME_HOURS = 2;
const REAUTHENTICATION_REQUEST_TIME_SECONDS = REAUTHENTICATION_REQUEST_TIME_HOURS * ONE_HOUR_IN_SECONDS;

export default class AuthService extends Service {
  @service account!: AccountService;
  @service api!: ApiService;
  @service masq!: MasqService;
  @service session!: SessionService;
  @service analytics!: AnalyticsService;

  @tracked
  isReauthenticating = false;

  // passed properties
  // Potentially undefined/null for a single tick when switching environments
  user?: UserModel;

  // computed properties
  @or('user.isReadOnly', 'masq.readOnly', 'account.current.isReadOnly') readOnly!: boolean;

  @readOnly('user.isViewer') viewer!: boolean;
  @readOnly('user.isRedacted') redacted!: boolean;
  @readOnly('user.role') role!: UserRoleType;

  @computed('role')
  get description(): string {
    return {
      [USER_ROLE_ADMIN]: "You have full access to your company's Customer.io account.",
      [USER_ROLE_MANAGER]: 'You have full access to your assigned workspaces.',
      [USER_ROLE_AUTHOR]: 'You have full access to make changes and send messages in your assigned workspaces.',
      [USER_ROLE_AUTHOR_REDACT]:
        'You have full access to make changes and send messages in your assigned workspaces. Data masked.',
      [USER_ROLE_VIEWER]: 'You have read-only access.',
      [USER_ROLE_VIEWER_REDACT]: 'You have read-only access. Data masked.',
    }[this.role];
  }

  @computed('account.current.twoFactorAuthenticationRequired', 'user.twoFactorAuthenticationEnabled')
  get twoFactorAuthenticationAccountRequirementFulfilled(): boolean {
    return (
      !this.account.current.twoFactorAuthenticationRequired ||
      Boolean(this.user?.twoFactorAuthenticationEnabled ?? false)
    );
  }

  get #globalReadOnly() {
    return (this.masq.isOn && this.masq.readOnly) || this.account.current.readOnly;
  }

  logout(byExplicitUserAction = false): Promise<unknown> {
    if (byExplicitUserAction) {
      this.session.set('loggingOutByExplicitUserAction', byExplicitUserAction);
    } else {
      this.analytics.track('user', 'logged_out_session_expired');
    }

    this.isReauthenticating = false;

    return this.api
      .post('logout', {
        pathScope: 'global',
        ignoreErrors: true,
      })
      .catch((error) => {
        if (error?.errors?.[0]?.status === '401') {
          // Ignore 401 errors on logout, which might happen if the session has already expired
          return;
        }
        throw error;
      })
      .finally(() => {
        // session clean-up failure on server-side shouldn't prevent browser session state from being cleared
        removeAccessCookie();
        return this.session.invalidate();
      });
  }

  invalidateExpiration() {
    removeAccessExpirationCookie();
    this.analytics.track('user', 'got_session_expiration_cookie_removed', {
      accountId: this.account?.current?.id,
      reason: 'expiration invalidated',
    });
  }

  checkAccessExpiration(): void {
    let tokenExpirationTime = parseInt(getAccessExpirationCookie() ?? '0');

    // Session expiration will be enforced only for users with a token expiration time, this can be bypassed if expiration time is null
    if (tokenExpirationTime > 0) {
      this.isReauthenticating = tokenExpirationTime <= unixNowSeconds();
    }
  }

  setAccessCookiesAndExpiration(accessToken: string, sessionExpirationTimeInSeconds: number | null) {
    setAccessCookie(accessToken);
    this.updateAccessExpirationCookie(sessionExpirationTimeInSeconds);
    this.isReauthenticating = false;
  }

  updateAccessExpirationCookie(sessionExpirationTimeInSeconds: number | null) {
    if (typeof sessionExpirationTimeInSeconds === 'number' && sessionExpirationTimeInSeconds > 0) {
      let accountId = this.account?.current?.id;
      this.analytics.track('user', 'got_session_expiration_timestamp', { data: { accountId } });
      // preventiveAccessExpiration when we show re-authentication options before access token expires
      let preventiveAccessExpiration = sessionExpirationTimeInSeconds - REAUTHENTICATION_REQUEST_TIME_SECONDS;
      setAccessExpirationCookie(preventiveAccessExpiration);

      // tracking the cookie has been set correctly
      let accessExpirationCookie = parseInt(getAccessExpirationCookie() ?? '0');
      if (typeof accessExpirationCookie === 'number' && accessExpirationCookie > 0) {
        this.analytics.track('user', 'got_session_expiration_cookie_set', { data: { accountId } });
      }
    } else {
      removeAccessExpirationCookie();
      this.analytics.track('user', 'got_session_expiration_cookie_removed', {
        accountId: this.account?.current?.id,
        reason: 'updated expiration',
      });
    }
  }

  _refreshAuthCookies = refreshAuthCookies;
  // called after each API request made from API service and Ember Data adapter, as a way to keep rolling auth cookies expiration date
  refreshAuthCookies() {
    if (!Ember.testing) {
      throttle(this, this._refreshAuthCookies, ONE_MINUTE_IN_SECONDS * 1000); // use ms
    }
  }

  hasPermission(permission: Permission) {
    // If the account/masq user is in read-only mode and it's not a view
    // permission, then they don't have permission to do the action
    if (this.#globalReadOnly && !permission.endsWith(Relation.View)) {
      return false;
    }

    if (this.masq.isOn && this.masq.readOnly === false) {
      return true;
    }

    if (!isValidPermission(permission)) {
      throw new Error(
        `Unknown permission: ${permission}. Permissions must be from the static set of permissions in @customerio/hydra-permissions.`,
      );
    }

    return this.user?.permissions[permission] ?? false;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    auth: AuthService;
  }
}
